/* ORYX App 2.0
 *
 * Created by ORYX Movement Solutions © 2022
 * ==================================================================
 *
 * Login form Component, including the logo.
 */

import { analytics } from '@/config/firebaseConfig';
import { IonInputCustomEvent } from '@ionic/core';
import { IonCol, IonGrid, IonImg, IonList, IonRow, IonText } from '@ionic/react';
import React, { useEffect, useState } from 'react';
import { z } from 'zod';

import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Toast } from '@/components/Toasts/Toast';

import { mergeClassNames } from '@/utils/mergeClassNames';

import { useAuthContext } from '@/contexts/authContext';

import Logo from '@/assets/oryx-logo.svg';

import styles from './loginForm.module.css';

export const ResetPasswordForm = () => {
  const [formSubmitted, setFormSubmitted] = useState(false);

  const [email, setEmail] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSend, setIsEmailSend] = useState(false);
  const [isValidEmail, setIsValidEmail] = useState(true);

  const { resetPassword, state } = useAuthContext();

  useEffect(() => {
    if (state.error) {
      if (import.meta.env.MODE !== 'production') console.log(state.error);
    }
  }, [state]);

  async function requestReset(e: React.FormEvent) {
    analytics.logEvent('resetPassword');
    e.preventDefault();
    setFormSubmitted(true);

    const emailValidationSchema = z.string().email();

    const result = emailValidationSchema.safeParse(email);

    setIsValidEmail(result.success);

    if (email && result.success) {
      setIsLoading(true);
      await resetPassword(email);
      setIsLoading(false);
      setIsEmailSend(true);

      // Ensures that if user submits the form again, the toast is dismissed
      setTimeout(() => {
        setIsEmailSend(false);
      }, 6_000);
    }
  }

  const handleEmail = (e: any) => {
    setEmail(e.detail.value);
  };

  function handleFocusEvent(ev: IonInputCustomEvent<FocusEvent>) {
    ev.target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <>
      <Toast
        header='Email address is invalid'
        isOpen={!isValidEmail}
        message={'The email address you entered is not valid. Please enter a valid email address.'}
        type='warning'
      />

      <Toast
        header='An email has been sent'
        isOpen={isEmailSend}
        message='Check your inbox to reset your password'
        type='info'
      />

      <form className={styles['login-form']} noValidate onSubmit={requestReset}>
        <IonGrid className={styles['no-padding']} fixed id='login-form'>
          <div>
            {import.meta.env.MODE !== 'production' && (
              <>
                <IonRow className={mergeClassNames('warning', styles.dev)}>
                  <IonCol>
                    <IonText style={{ color: '#ffffff' }}>{import.meta.env.MODE} environment!</IonText>
                  </IonCol>
                </IonRow>
              </>
            )}

            <IonRow>
              <IonCol className={styles['no-padding']} size='12'>
                <IonImg className={styles.logo} src={Logo}></IonImg>
              </IonCol>
            </IonRow>
          </div>
          <div className={styles.main} id='focus-target'>
            <IonRow>
              <IonCol className={styles.copy} size='12'>
                <IonText className={styles.heading}>Reset your password</IonText>
                <IonText className={styles.description}>Enter your user account&apos;s verified email address.</IonText>
              </IonCol>
            </IonRow>

            <IonRow className={styles['no-padding']}>
              <IonCol class={styles['no-padding']} size='12'>
                <IonList class={mergeClassNames(styles['no-padding'], styles.list)} mode='md'>
                  <Input
                    hasError={!isValidEmail}
                    label='Email'
                    label-placement='stacked'
                    name='email'
                    onIonFocus={handleFocusEvent}
                    onIonInput={handleEmail}
                    type='email'
                  />
                </IonList>
              </IonCol>
            </IonRow>
            <IonRow class={styles['no-padding']}>
              <IonCol class={mergeClassNames(styles['button-wrapper'], styles['no-padding'])} size='12'>
                <Button className={styles.button} isFullWidth isLoading={isLoading} type='submit' variant='primary'>
                  Reset password
                </Button>
              </IonCol>
            </IonRow>
          </div>
        </IonGrid>
      </form>
    </>
  );
};
